/**
 * What a converter returns: the records it read, and what it could not carry over.
 * Warnings are codes with a record number, so the widget can word them in any language.
 */

import type { RefRecord } from "@/lib/tool-kit/research-discovery/records";

export interface ConvertWarning {
  code: string;
  /** 1-based position of the record in the source, or 0 for the whole file. */
  record: number;
  /** A value to show beside the message (an unrecognised type name, say). */
  detail?: string;
}

export interface ConvertResult {
  records: RefRecord[];
  warnings: ConvertWarning[];
  /** Records found in the source, whether or not they converted. */
  sourceCount: number;
  /** Set when nothing could be read: the code of the reason. */
  problem?: string;
}

const MONTHS: Record<string, number> = {
  jan: 1,
  feb: 2,
  mar: 3,
  apr: 4,
  may: 5,
  jun: 6,
  jul: 7,
  aug: 8,
  sep: 9,
  oct: 10,
  nov: 11,
  dec: 12,
};

const RE_YEAR = /\b(1[5-9]\d\d|20\d\d)\b/;
const RE_NUMERIC_DATE = /^(\d{4})[/.-](\d{1,2})(?:[/.-](\d{1,2}))?/;
const RE_MONTH_WORD = /\b([A-Za-z]{3})[A-Za-z]*\.?/;
const RE_DAY = /\b(\d{1,2})\b/;

function pad(value: number): string {
  return String(value).padStart(2, "0");
}

/**
 * A date as YYYY, YYYY/MM or YYYY/MM/DD from a year and whatever date text accompanies
 * it: "2014/05/01", "May 5", "Jan", "2014 Mar 15". Anything it cannot read is ignored,
 * leaving the year alone — a wrong month is worse than none.
 */
export function composeDate(yearText: string, dateText: string): string {
  const numeric = RE_NUMERIC_DATE.exec(dateText.trim());
  if (numeric) {
    const month = Number(numeric[2]);
    const day = Number(numeric[3] ?? 0);
    if (month >= 1 && month <= 12) {
      return day >= 1 && day <= 31
        ? `${numeric[1]}/${pad(month)}/${pad(day)}`
        : `${numeric[1]}/${pad(month)}`;
    }
  }
  const year = RE_YEAR.exec(yearText)?.[1] ?? RE_YEAR.exec(dateText)?.[1] ?? "";
  if (year === "") {
    return "";
  }
  const monthWord = RE_MONTH_WORD.exec(dateText.replace(year, ""));
  const month = monthWord ? MONTHS[monthWord[1].toLowerCase()] : undefined;
  if (!month) {
    return year;
  }
  const day = Number(RE_DAY.exec(dateText.replace(year, ""))?.[1] ?? 0);
  return day >= 1 && day <= 31
    ? `${year}/${pad(month)}/${pad(day)}`
    : `${year}/${pad(month)}`;
}

const RE_DOI = /10\.\d{4,9}\/[^\s"<>]+/;
const RE_DOI_TAIL = /[.,;:)\]]+$/;
const RE_ISSN = /^\d{4}-\d{3}[\dXx]$/;
const RE_SPACES = /\s+/;

/** The DOI inside a string such as "doi:10.1000/x", a doi.org URL or a bare DOI. */
export function extractDoi(value: string): string {
  const match = RE_DOI.exec(value);
  return match ? match[0].replace(RE_DOI_TAIL, "").toLowerCase() : "";
}

/** Sort an ISSN/ISBN field into the right slot. */
export function classifyStandardNumber(value: string): {
  issn: string;
  isbn: string;
} {
  const text = value.trim();
  if (text === "") {
    return { issn: "", isbn: "" };
  }
  return RE_ISSN.test(text.split(RE_SPACES)[0] ?? "")
    ? { issn: text, isbn: "" }
    : { issn: "", isbn: text };
}
